import { useQuery } from '@tanstack/react-query';
import apiClient from '../api-client';
import { normalizeTeamLogoUrl } from '../../utils/normalizeTeamLogoUrl';

export type SquaresContestResponse = {
  squares_contest_id: string;
  pickem_id?: string; // New field from question-based system
  question_id?: string; // New field from question-based system
  tenant_id: string;
  game_type: string;
  status: string;
  contest_name_en?: string;
  contest_name_fr?: string;
  contest_subtext_en?: string;
  contest_subtext_fr?: string;
  entry_fee: number;
  max_squares_per_user: number;
  start_time_utc?: string;
  selection_editable_until_utc?: string; // Maps to open_time_utc
  open_time_utc?: string; // Alias for selection_editable_until_utc
  row_team_id?: string;
  col_team_id?: string;
  row_team_display_name?: string;
  col_team_display_name?: string;
  row_team_abbreviation?: string;
  col_team_abbreviation?: string;
  row_team_bg_color?: string;
  col_team_bg_color?: string;
  row_team_icon_url?: string;
  col_team_icon_url?: string;
  sport: string;
  fixture_id?: string;
  scheduled_game_start_utc?: string;
  total_prize_amount: number;
  total_entries_to_date: number;
  grid_rows: number;
  grid_cols: number;
  square_count: number;
  // New fields from question-based system
  row_digits?: number[]; // Random digit assignment for rows
  col_digits?: number[]; // Random digit assignment for columns
  periods?: Array<{
    periodCode: string;
    label: string;
    displayOrder: number;
    endCondition: string;
    prizeAmount: number;
  }>;
  gam_enabled?: boolean;
  google_manager_sdk_enabled?: boolean;
  ad_units?: Array<any>;
  // Custom background assets
  badge_image_url?: string;
  rules_image_url?: string;
  /*
   * Two device pairs. The non-mobile key is the desktop/default asset — shown on every device
   * when the mobile one is absent, so a contest carrying only it renders exactly as before.
   */
  background_image_url?: string;
  background_image_mobile_url?: string;
  submit_view_image_url?: string;
  submit_view_image_mobile_url?: string;
  // Reserve a Seat (DKPLAT-529) — shared infra with Pick'em (DKPLAT-528)
  questions_posted_at?: string | null;
  reserve_a_seat_enabled?: boolean;
  has_reservation?: boolean;
  has_selections?: boolean;
  has_entered?: boolean;
  [key: string]: any;
};

type GetSquaresResponse = {
  squares_contests: SquaresContestResponse[];
};

const normalizeContestTeamIcons = (contest: SquaresContestResponse): SquaresContestResponse => ({
  ...contest,
  row_team_icon_url: contest.row_team_icon_url
    ? normalizeTeamLogoUrl(contest.row_team_icon_url)
    : contest.row_team_icon_url,
  col_team_icon_url: contest.col_team_icon_url
    ? normalizeTeamLogoUrl(contest.col_team_icon_url)
    : contest.col_team_icon_url,
});

interface GetSquaresParams {
  limit?: number;
  offset?: number;
  status?: string;
}

export const getSquares = (params?: GetSquaresParams) =>
  apiClient.get<GetSquaresResponse>('/api/squares', { params });

export const useGetSquaresQuery = (params?: GetSquaresParams, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['squares', params],
    queryFn: () => getSquares(params),
    select: data => (data.squares_contests || []).map(normalizeContestTeamIcons),
    enabled: options?.enabled !== undefined ? options.enabled : true,
  });
};

type GetSquareResponse = {
  squares_contest: SquaresContestResponse;
};

export const getSquare = (id: string) => apiClient.get<GetSquareResponse>(`/api/squares/${id}`);

export const useGetSquareQuery = (id: string | null) => {
  return useQuery({
    queryKey: ['square', id],
    queryFn: () => getSquare(id!),
    select: data => normalizeContestTeamIcons(data.squares_contest),
    enabled: !!id,
  });
};
